import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, 
  Cell, PieChart, Pie
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, ChevronRight, 
  Zap, Calendar, ArrowUpRight, ArrowDownRight, AlertCircle
} from 'lucide-react';

// Import mock data
import { salesByDate, storePerformance } from '../../data/analyticsData';

// Helper for formatting currency
const formatCurrency = (value) => {
  return value.toLocaleString('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Helper for formatting percentages
const formatPercent = (value) => {
  return `${value.toFixed(1)}%`;
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, includeTax }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        <div className="space-y-1.5">
          <p className="text-sm text-blue-600">
            {includeTax ? 'Sales (inc. VAT): ' : 'Net Sales: '}
            {formatCurrency(payload[0].value)}
          </p>
          {payload[1] && (
            <p className="text-sm text-green-600">
              Transactions: {payload[1].value.toLocaleString()}
            </p>
          )}
          {payload[2] && (
            <p className="text-sm text-purple-600">
              Avg. Ticket: {formatCurrency(payload[2].value)}
            </p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

// Sales metric card component
const SalesMetricCard = ({ title, value, previousValue, icon: Icon, color = 'blue' }) => {
  const percentChange = previousValue ? ((value - previousValue) / previousValue) * 100 : 0;
  const isPositive = percentChange >= 0;
  
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={`p-2 rounded-lg bg-${color}-50 dark:bg-${color}-900/20`}>
              <Icon className={`w-5 h-5 text-${color}-500 dark:text-${color}-400`} />
            </div>
          )}
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
        </div>
        <div className={`flex items-center text-sm ${
          isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {isPositive ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          <span>{Math.abs(percentChange).toFixed(1)}%</span>
        </div>
      </div>
      
      <div className="mt-2">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {typeof value === 'number' ? formatCurrency(value) : value}
        </p>
      </div>
    </div>
  );
};

const SalesAnalysis = ({ filters }) => {
  const [chartType, setChartType] = useState('area');
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    currentPeriod: {
      sales: 0,
      transactions: 0,
      avgTicket: 0,
      growth: 0
    },
    previousPeriod: {
      sales: 0,
      transactions: 0,
      avgTicket: 0
    }
  });
  const [breakdownData, setBreakdownData] = useState([]);
  
  // Determine which date range to use based on filters
  useEffect(() => {
    let selectedData;
    
    // Select the appropriate data based on the date range
    switch(filters.dateRange) {
      case 'today':
      case 'yesterday':
      case '7d':
        selectedData = salesByDate.daily.slice(-7);
        break;
      case '30d':
        selectedData = salesByDate.daily;
        break;
      case 'mtd':
        selectedData = salesByDate.daily.slice(-new Date().getDate());
        break;
      case 'ytd':
        selectedData = salesByDate.monthly.slice(0, new Date().getMonth() + 1);
        break;
      default:
        selectedData = salesByDate.daily;
    }
    
    // Process data for visualization
    const processedData = selectedData.map(item => ({
      ...item,
      sales: filters.includeTax ? item.salesWithVAT : item.netSales,
      avgTicket: filters.includeTax 
        ? item.salesWithVAT / item.transactions 
        : item.netSales / item.transactions
    }));
    
    setData(processedData);
    
    // Calculate summary stats
    const totalSales = processedData.reduce((sum, item) => sum + item.sales, 0);
    const totalTransactions = processedData.reduce((sum, item) => sum + item.transactions, 0);
    const avgTicket = totalSales / totalTransactions;
    
    // Calculate previous period for comparison (simple approach for demo)
    const prevSales = totalSales * 0.9; // 10% less for demonstration
    const prevTransactions = totalTransactions * 0.92;
    const prevAvgTicket = prevSales / prevTransactions;
    
    setSummary({
      currentPeriod: {
        sales: totalSales,
        transactions: totalTransactions,
        avgTicket: avgTicket,
        growth: ((totalSales - prevSales) / prevSales) * 100
      },
      previousPeriod: {
        sales: prevSales,
        transactions: prevTransactions,
        avgTicket: prevAvgTicket
      }
    });
    
    // Generate breakdown by store type
    // In a real app, this would filter based on the actual store type
    // For this demo, we'll create synthetic data
    const storeTypes = ['Café', 'Café+DT', 'Drive-thru', 'Kiosk'];
    const breakdown = storeTypes.map(type => {
      // Calculate percentage based on store type (just for demo)
      let percentage;
      switch(type) {
        case 'Café': percentage = 0.35; break;
        case 'Café+DT': percentage = 0.40; break;
        case 'Drive-thru': percentage = 0.20; break;
        case 'Kiosk': percentage = 0.05; break;
        default: percentage = 0.25;
      }
      
      return {
        name: type,
        value: totalSales * percentage,
        percentage: percentage * 100
      };
    });
    
    setBreakdownData(breakdown);
    
  }, [filters]);
  
  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SalesMetricCard 
          title="Total Sales"
          value={summary.currentPeriod.sales}
          previousValue={summary.previousPeriod.sales}
          icon={DollarSign}
          color="blue"
        />
        <SalesMetricCard 
          title="Transactions"
          value={summary.currentPeriod.transactions}
          previousValue={summary.previousPeriod.transactions}
          icon={Calendar}
          color="green"
        />
        <SalesMetricCard 
          title="Average Ticket"
          value={summary.currentPeriod.avgTicket}
          previousValue={summary.previousPeriod.avgTicket}
          icon={TrendingUp}
          color="purple"
        />
        <SalesMetricCard 
          title="YoY Growth"
          value={`${summary.currentPeriod.growth.toFixed(1)}%`}
          previousValue={null}
          icon={Zap}
          color="amber"
        />
      </div>
      
      {/* Main Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-wrap items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {filters.includeTax ? 'Sales (incl. VAT)' : 'Net Sales'} Trend
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {filters.dateRange === '7d' ? 'Last 7 days' : 
               filters.dateRange === '30d' ? 'Last 30 days' :
               filters.dateRange === 'mtd' ? 'Month to date' :
               filters.dateRange === 'ytd' ? 'Year to date' : 'Selected period'}
            </p>
          </div>
          
          {/* Chart Type Selector */}
          <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-1">
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded ${
                chartType === 'bar' 
                  ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Bar
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded ${
                chartType === 'line' 
                  ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`p-2 rounded ${
                chartType === 'area' 
                  ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Area
            </button>
            <button
              onClick={() => setChartType('composed')}
              className={`p-2 rounded ${
                chartType === 'composed' 
                  ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Combined
            </button>
          </div>
        </div>
        
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={data} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
                />
                <Tooltip content={<CustomTooltip includeTax={filters.includeTax} />} />
                <Legend />
                <Bar 
                  dataKey="sales" 
                  name={filters.includeTax ? "Sales (incl. VAT)" : "Net Sales"} 
                  fill="#3b82f6" 
                />
              </BarChart>
            ) : chartType === 'line' ? (
              <LineChart data={data} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
                />
                <Tooltip content={<CustomTooltip includeTax={filters.includeTax} />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="sales" 
                  name={filters.includeTax ? "Sales (incl. VAT)" : "Net Sales"} 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            ) : chartType === 'area' ? (
              <AreaChart data={data} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
                />
                <Tooltip content={<CustomTooltip includeTax={filters.includeTax} />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="sales" 
                  name={filters.includeTax ? "Sales (incl. VAT)" : "Net Sales"} 
                  fill="#3b82f6" 
                  fillOpacity={0.3}
                  stroke="#3b82f6" 
                  strokeWidth={2}
                />
              </AreaChart>
            ) : (
              <ComposedChart data={data} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip includeTax={filters.includeTax} />} />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="sales" 
                  name={filters.includeTax ? "Sales (incl. VAT)" : "Net Sales"} 
                  fill="#3b82f6" 
                  fillOpacity={0.8}
                  barSize={20}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="transactions" 
                  name="Transactions" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="avgTicket" 
                  name="Avg. Ticket" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Additional Analysis - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Sales Breakdown by Store Type
          </h3>
          
          <div className="flex items-stretch h-[300px]">
            <div className="w-1/2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={breakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {breakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="w-1/2 flex flex-col justify-center">
              <div className="space-y-3">
                {breakdownData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.name}</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Regional Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top Performing Regions
          </h3>
          
          <div className="space-y-4">
            {/* Group and sum sales by region */}
            {[...new Set(storePerformance.map(store => store.region))].map((region, index) => {
              const regionStores = storePerformance.filter(store => store.region === region);
              const regionSales = regionStores.reduce((sum, store) => sum + store.sales, 0);
              const storeCount = regionStores.length;
              const avgStorePerformance = regionSales / storeCount;
              
              // Get growth as average of store growths
              const avgGrowth = regionStores.reduce((sum, store) => sum + store.growth, 0) / storeCount;
              
              return (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{region}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {storeCount} stores
                      </p>
                    </div>
                    <div className={`flex items-center text-sm ${
                      avgGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {avgGrowth >= 0 ? (
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 mr-1" />
                      )}
                      <span>{Math.abs(avgGrowth).toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex justify-between items-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(regionSales)}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span>Avg. Store: {formatCurrency(avgStorePerformance)}</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Insights Box */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800/30">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-300">Sales Insights</h3>
            <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Café+DT store type shows the strongest performance with 40% of total sales.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Sales are trending upward with a {summary.currentPeriod.growth.toFixed(1)}% increase over the previous period.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Average transaction value has increased by {(((summary.currentPeriod.avgTicket / summary.previousPeriod.avgTicket) - 1) * 100).toFixed(1)}%, indicating higher spending per customer.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Riyadh Region continues to be the top performing region by total sales.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesAnalysis; 